// WHAT: Central layout component for the entire application
// WHY:  Provides main app structure with sidebar, header, footer, and content area
// DOMAIN: Core → Layout
// DEPENDENCIES: React Router, Header, Sidebar, Footer, CustomTitlebar components
// RISKS:
//   - NOTE: Grid layout is fixed - may not work well on very small screens
//   - NOTE: Auto-redirect in DEV mode may be unexpected
//   - NOTE: Title generation is simple - may not match all routes
//   - NOTE: Minimum width constraints may cause issues on small screens

import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { ModernSidebar } from "@/components/navigation/ModernSidebar";
import { CustomTitlebar } from "@/components/organisms/CustomTitlebar";
import { Footer } from "@/components/organisms/Footer";
import { Header } from "@/components/organisms/Header";
import { PathProvider } from "@/shared/contexts";

import { CONTENT_PADDING_RIGHT } from "./SidebarLayoutWidth";
import { useSidebar } from "./useSidebar";

// NOTE: Layout Components

/**
 * WHAT:
 *   Central layout component that provides the main application structure
 *   with sidebar, header, footer, and content area.
 *
 * WHY:
 *   Defines the overall app layout and routing structure with consistent
 *   styling and navigation.
 *
 * BEHAVIOR:
 *   - Auto-redirects root path to dashboard in DEV mode
 *   - Updates document title based on current route
 *   - Manages sidebar state (open/closed)
 *   - Provides grid layout for all sections
 *   - Wraps content in PathProvider for path context
 *
 * RISKS:
 *   - NOTE: Grid layout is fixed - may not work well on very small screens
 *   - NOTE: Auto-redirect in DEV mode may be unexpected
 *   - NOTE: Title generation is simple - may not match all routes
 *   - NOTE: Minimum width constraints may cause issues on small screens
 *
 * @returns JSX element with app layout structure
 *
 */
export const AppLayout = () => {
  const { isOpen } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();

  // NOTE: Auto-redirect root path to dashboard in DEV mode
  useEffect(() => {
    if (import.meta.env.DEV && location.pathname === "/") {
      const result = navigate("/dashboard");
      if (result instanceof Promise) {
        result.catch(() => {
          // NOTE: Navigation error handling - silently ignore to prevent UI disruption
        });
      }
    }
  }, [location.pathname, navigate]);

  // NOTE: Simple title generation based on route
  useEffect(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const lastSegment = segments.at(-1) ?? "Dashboard";
    const title = `TracQ – ${lastSegment.charAt(0).toUpperCase() + lastSegment.slice(1)}`;
    document.title = title;
  }, [location.pathname]);

  return (
    <PathProvider>
      <div
        className="app-layout h-screen w-screen overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-slate-100"
        style={{
          display: "grid",
          gap: "0",
          gridTemplateColumns: isOpen ? "320px 1fr" : "80px 1fr",
          gridTemplateRows: "40px 1fr auto",
          minWidth: "800px",
        }}
      >
        {/* Custom Titlebar */}
        <div style={{ gridColumn: "1 / -1", gridRow: "1" }}>
          <CustomTitlebar />
        </div>
        {/* Sidebar */}
        <div
          className="sidebar-container"
          style={{
            gridColumn: "1",
            gridRow: "2 / -1",
            height: "100%", // ✅ bleibt im Grid-Kontext
            overflowX: "hidden", // ✅ Horizontaler Scrollbar entfernt
            overflowY: "auto", // ✅ Eine Scrollbar für die gesamte Sidebar
          }}
        >
          <ModernSidebar />
        </div>

        {/* Main Content */}
        <div
          className="main-content flex flex-col overflow-hidden"
          style={{
            gridColumn: "2",
            gridRow: "2",
            minWidth: "600px",
            paddingRight: CONTENT_PADDING_RIGHT,
          }}
        >
          <div className="flex-shrink-0 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
            <Header />
          </div>

          <main className="flex-1 overflow-y-auto py-6 px-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
            <Outlet />
          </main>
        </div>

        {/* Footer */}
        <div style={{ gridColumn: "2", gridRow: "3" }}>
          <Footer />
        </div>
      </div>
    </PathProvider>
  );
};
